'use client';

import React from 'react';
import Image from 'next/image';
import Box from '@mui/material/Box';
import sanitize from '../shared/sanitize';

import BuilderInvestButton from '../BuilderInvestButton';
import {
  StripContainer,
  StripContent,
  SectionLabel,
  Title,
  TitleHighlight,
  Subtitle,
  BodyText,
  BannerImage,
} from './styles';
import {
  getBackgroundGradient,
  getScaledSpacing,
  getSectionBackgroundStyles,
} from '../shared/backgroundStyles';
import { sectionBorderRadiusSx } from '../shared/sectionBorderRadius';
import useCustomFont from '../shared/useCustomFont';

const sanitizeHtml = (html) => {
  if (!html) return '';
  return sanitize(html);
};

const isEmptyHtml = (html) => {
  if (!html) return true;
  const stripped = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return stripped.length === 0;
};

const BuilderBannerSection = ({
  sectionId = '',
  sectionBorderRadius = '0px',
  sectionLabel = '',
  sectionLabelColor = '#111827',
  sectionLabelFontSize = '14px',
  sectionLabelSuperscript = '',
  title = '',
  titleColor = '#111827',
  titleFontWeight = 400,
  titleHighlight = '',
  titleHighlightColor = '#185C65',
  titleSuperscript = '',
  titleHighlightItalic = true,
  titleMarginBottom = '16px',
  subtitle = '',
  subtitleColor = 'rgba(255,255,255,0.9)',
  textAlign = 'left',
  bannerImage = '',
  bannerMediaType = 'image',
  bannerImageAlt = '',
  bannerBorderRadius = '8px',
  bannerMaxWidth = '100%',
  bannerAlignment = 'left',
  bannerSpacingTop = '24px',
  bannerSpacingBottom = '24px',
  textAbove = '',
  textBelow = '',
  textColor = '#374151',
  textFontSize = '18px',
  textLineHeight = '1.8',
  verticalPadding = 60,
  maxWidth = 1200,
  backgroundColor = 'transparent',
  backgroundImage = '',
  backgroundImageMobile = '',
  backgroundOverlayColor = 'rgba(0, 0, 0, 0.5)',
  backgroundGradient = '',
  backgroundGradientEnabled = false,
  backgroundGradientType = 'linear',
  backgroundGradientDirection = 'to bottom',
  backgroundGradientAngle = 180,
  backgroundGradientStops = [],
  titleFontSource = 'default',
  titleAdobeFontName = 'owners-wide',
  titleGoogleFontName = 'Playfair Display',
  titleGoogleFontCustom = '',
  titleGoogleFontWeights = '400;500;700',
  titleCustomFontUrl = '',
  titleCustomFontFamily = '',
  bodyFontSource = 'default',
  bodyAdobeFontName = 'owners-text',
  bodyGoogleFontName = 'Open Sans',
  bodyGoogleFontCustom = '',
  bodyGoogleFontWeights = '400;500;700',
  bodyCustomFontUrl = '',
  bodyCustomFontFamily = '',
  showInvestButton = false,
  investButtonSpacing = '32px',
  investButtonText = 'Get Shares Now',
  investButtonBackgroundColor = '',
  investButtonTextColor = '',
  investButtonBorderRadius = '16px',
  investButtonWidth = '100%',
  investButtonMaxWidth = '327px',
  investButtonPadding = '12px 24px',
}) => {
  const { fontFamily: titleFont } = useCustomFont({
    fontSource: titleFontSource,
    adobeFontName: titleAdobeFontName,
    googleFontName: titleGoogleFontName,
    googleFontCustom: titleGoogleFontCustom,
    googleFontWeights: titleGoogleFontWeights,
    customFontUrl: titleCustomFontUrl,
    customFontFamily: titleCustomFontFamily,
  });

  const { fontFamily: bodyFont } = useCustomFont({
    fontSource: bodyFontSource,
    adobeFontName: bodyAdobeFontName,
    googleFontName: bodyGoogleFontName,
    googleFontCustom: bodyGoogleFontCustom,
    googleFontWeights: bodyGoogleFontWeights,
    customFontUrl: bodyCustomFontUrl,
    customFontFamily: bodyCustomFontFamily,
  });

  const resolvedTitleFont =
    titleFontSource !== 'default' ? titleFont : 'var(--builder-title-font, inherit)';
  const resolvedBodyFont =
    bodyFontSource !== 'default' ? bodyFont : 'var(--builder-body-font, inherit)';

  const hasTextAbove = !isEmptyHtml(textAbove);
  const hasTextBelow = !isEmptyHtml(textBelow);

  const backgroundGradientValue = getBackgroundGradient({
    backgroundGradient,
    backgroundGradientEnabled,
    backgroundGradientType,
    backgroundGradientDirection,
    backgroundGradientAngle,
    backgroundGradientStops,
  });
  const hasBackgroundImage = Boolean(backgroundImage || backgroundImageMobile);
  const hasGradient = Boolean(backgroundGradientValue);

  const containerStyles = {
    fontFamily: resolvedBodyFont,
    ...getSectionBackgroundStyles({
      backgroundColor,
      backgroundGradient: backgroundGradientValue,
      backgroundImage,
      backgroundImageMobile,
    }),
  };

  return (
    <StripContainer
      id={sectionId || undefined}
      sx={{ ...containerStyles, ...sectionBorderRadiusSx(sectionBorderRadius) }}
      data-cy="builder-banner-section"
    >
      {hasBackgroundImage && !hasGradient && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: backgroundOverlayColor,
            zIndex: 0,
          }}
        />
      )}
      <StripContent
        sx={{
          maxWidth: `${maxWidth}px`,
          padding: {
            xs: `${Math.max(verticalPadding * 0.65, 24)}px 16px ${showInvestButton ? 0 : Math.max(verticalPadding * 0.65, 24)}px`,
            md: `${verticalPadding}px 24px ${showInvestButton ? 0 : verticalPadding}px`,
          },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {sectionLabel && (
          <SectionLabel
            component="p"
            sx={{
              color: sectionLabelColor,
              fontSize: sectionLabelFontSize,
              fontFamily: resolvedTitleFont,
              textAlign,
            }}
          >
            {sectionLabel}
            {sectionLabelSuperscript && <sup>{sectionLabelSuperscript}</sup>}
          </SectionLabel>
        )}

        {title && (
          <Title
            component="h2"
            sx={{
              color: titleColor,
              fontWeight: titleFontWeight,
              fontFamily: resolvedTitleFont,
              textAlign,
              marginBottom: titleMarginBottom,
            }}
          >
            {titleHighlight && title.includes(titleHighlight) ? (
              <>
                {title.split(titleHighlight)[0]}
                <TitleHighlight
                  sx={{
                    color: titleHighlightColor,
                    fontStyle: titleHighlightItalic ? 'italic' : 'normal',
                  }}
                >
                  {titleHighlight}
                </TitleHighlight>
                {title.split(titleHighlight)[1] || ''}
              </>
            ) : (
              title
            )}
            {titleSuperscript && <sup>{titleSuperscript}</sup>}
          </Title>
        )}

        {subtitle && (
          <Subtitle
            sx={{
              color: subtitleColor,
              fontFamily: resolvedBodyFont,
              textAlign,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(subtitle) }}
          />
        )}

        {hasTextAbove && (
          <BodyText
            sx={{
              color: textColor,
              fontSize: textFontSize,
              lineHeight: textLineHeight,
              fontFamily: resolvedBodyFont,
              textAlign,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(textAbove) }}
          />
        )}

        {bannerImage && (
          <BannerImage
            sx={{
              borderRadius: bannerBorderRadius,
              maxWidth: bannerMaxWidth,
              marginTop: hasTextAbove || title || subtitle || sectionLabel ? bannerSpacingTop : 0,
              marginBottom: hasTextBelow ? bannerSpacingBottom : 0,
              alignSelf: (() => {
                if (bannerAlignment === 'center') return 'center';
                if (bannerAlignment === 'right') return 'flex-end';
                return 'flex-start';
              })(),
            }}
          >
            {bannerMediaType === 'video' ? (
              <video
                src={bannerImage}
                autoPlay
                loop
                muted
                playsInline
                style={{ borderRadius: bannerBorderRadius }}
              />
            ) : (
              <Image
                src={bannerImage}
                alt={bannerImageAlt || sectionLabel || 'Banner'}
                width={1200}
                height={675}
                priority={true}
                sizes="100vw"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: bannerBorderRadius,
                }}
              />
            )}
          </BannerImage>
        )}

        {hasTextBelow && (
          <BodyText
            sx={{
              color: textColor,
              fontSize: textFontSize,
              lineHeight: textLineHeight,
              fontFamily: resolvedBodyFont,
              textAlign,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(textBelow) }}
          />
        )}
      </StripContent>
      {showInvestButton && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            maxWidth: `${maxWidth}px`,
            margin: '0 auto',
            marginTop: investButtonSpacing,
            padding: `0 24px ${getScaledSpacing(verticalPadding)}`,
            position: 'relative',
            zIndex: 1,
          }}
          data-cy="builder-section-invest-cta"
        >
          <BuilderInvestButton
            buttonText={investButtonText}
            backgroundColor={investButtonBackgroundColor}
            textColor={investButtonTextColor}
            borderRadius={investButtonBorderRadius}
            width={investButtonWidth}
            maxWidth={investButtonMaxWidth}
            padding={investButtonPadding}
            sectionName="banner"
          />
        </Box>
      )}
    </StripContainer>
  );
};

export default BuilderBannerSection;
